import React, { useCallback, useMemo, useState } from "react";
import { SavedMessage } from "../../types/chat.types";
import { MarkdownRenderer } from "../../utils/markdownRenderer";
import PdfViewer from "../PdfViewer/PdfViewer";
import "./SavedMessageItem.css";
import "../Message/Markdown.css";

interface SavedMessageItemProps {
  message: SavedMessage;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onDelete: () => void;
  truncateText: (text: string, maxLength?: number) => string;
}

const ExpandButton: React.FC<{
  isExpanded: boolean;
  onClick: () => void;
}> = ({ isExpanded, onClick }) => (
  <button
    className="action-btn expand-btn"
    onClick={onClick}
    title={isExpanded ? "Einklappen" : "Mehr anzeigen"}
    aria-label={isExpanded ? "Nachricht einklappen" : "Nachricht erweitern"}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d={isExpanded ? "M18 15L12 9L6 15" : "M6 9L12 15L18 9"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const DeleteButton: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => (
  <button
    className="action-btn delete-btn"
    onClick={onClick}
    title="Nachricht löschen"
    aria-label="Gespeicherte Nachricht löschen"
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 6H5H21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const ReferenceChip: React.FC<{
  reference: any;
  onClick: (ref: any) => void;
}> = ({ reference, onClick }) => {
  // Format document name (GA013.pdf -> GA013)
  const formatDocName = (name: string) => {
    if (!name) return "Unbekannt";
    return name.replace(/\.pdf$/i, "");
  };

  const docName = formatDocName(reference.document_name);
  const pageNum = reference.page_number;

  // Build display text
  const displayText = pageNum ? `${docName}, S. ${pageNum}` : docName;
  const tooltipText = pageNum
    ? `${docName} - Seite ${pageNum}`
    : docName;

  return (
    <div
      className="saved reference-chip clickable-reference"
      onClick={() => onClick(reference)}
      title={`Klicken um Dokument anzuzeigen: ${tooltipText}`}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        style={{ marginRight: "6px" }}
      >
        <path
          d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 2V8H20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="reference-title">{displayText}</span>
    </div>
  );
};

const formatDate = (timestamp: string): string => {
  const date = new Date(timestamp);
  return `${date.toLocaleDateString("de-DE")} ${date.toLocaleTimeString(
    "de-DE",
    { hour: "2-digit", minute: "2-digit" }
  )}`;
};

const SavedMessageItem: React.FC<SavedMessageItemProps> = React.memo(({
  message,
  isExpanded,
  onToggleExpanded,
  onDelete,
  truncateText,
}) => {
  const [selectedReference, setSelectedReference] = useState<any>(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [isDirectLink, setIsDirectLink] = useState(false);

  const references = useMemo(() => message.references || [], [message.references]);
  const hasReferences = references.length > 0;

  // Memoize deduplicated references
  const uniqueReferences = useMemo(() =>
    references.reduce((acc: any[], ref: any) => {
      const existingIndex = acc.findIndex(item => item.document_name === ref.document_name);
      if (existingIndex === -1) {
        acc.push(ref);
      } else if (ref.similarity > acc[existingIndex].similarity) {
        acc[existingIndex] = ref;
      }
      return acc;
    }, []), [references]);

  const handleReferenceClick = useCallback((reference: any, directLink: boolean = false) => {
    const searchResult = {
      document_name: reference.document_name,
      excerpt: reference.excerpt || reference.content || "",
      context: reference.context || "",
      index: reference.index,
      marker: reference.marker || "",
      similarity: reference.similarity,
      page_number: reference.page_number
    };
    setSelectedReference(searchResult);
    setIsDirectLink(directLink);
    setPdfViewerOpen(true);
  }, []);

  const closePdfViewer = useCallback(() => {
    setPdfViewerOpen(false);
    setSelectedReference(null);
  }, []);

  return (
    <>
      <div className="saved-message-item">
        <div className="message-header">
          <div className="message-date">{formatDate(message.timestamp)}</div>
          <div className="message-actions">
            <ExpandButton isExpanded={isExpanded} onClick={onToggleExpanded} />
            <DeleteButton onClick={onDelete} />
          </div>
        </div>

        <div className="message-content">
          {isExpanded ? (
            <div className="expanded-content">
              <div className="full-text">
                <MarkdownRenderer 
                  content={message.content}
                  references={references}
                  onReferenceClick={handleReferenceClick}
                />
              </div>
              {hasReferences && (
                <div className="saved references-section">
                  <div className="saved references-title">Quellen:</div>
                  <div className="saved references-list">
                    {uniqueReferences.map((ref) => (
                      <ReferenceChip
                        key={ref.document_name}
                        reference={ref}
                        onClick={handleReferenceClick}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="truncated-content">
              {truncateText(message.content)}
            </div>
          )}
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {selectedReference && (
        <PdfViewer
          open={pdfViewerOpen}
          onClose={closePdfViewer}
          reference={selectedReference}
          isDirectLink={isDirectLink}
        />
      )}
    </>
  );
});

export default SavedMessageItem;
